import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import styles from './copilot.module.css';
import { ActionsPanel } from './ActionsPanel';

type CopilotPlan = {
  id: string;
  type: string;
  status: string;
  objective: string;
  version: number;
  missingFields: { key: string; label: string; priority: string }[];
  planData?: {
    audience?: {
      description?: string;
      locations?: string[];
      industries?: string[];
      companySize?: { min?: number; max?: number };
      jobRoles?: string[];
    };
    campaign?: { channel?: string; offer?: string };
  } | null;
};

const STATUS_LABEL: Record<string, string> = {
  collecting_information: 'Informatie verzamelen',
  planning: 'Plan uitwerken',
  ready_for_review: 'Klaar voor review',
  needs_revision: 'Aanpassen',
  cancelled: 'Geannuleerd',
  completed: 'Afgerond',
};

const TYPE_LABEL: Record<string, string> = {
  CAMPAIGN_CREATION: 'Campagneplan',
  AUDIENCE_CREATION: 'Doelgroepplan',
  MARKETING_STRATEGY: 'Marketingstrategie',
  CAMPAIGN_OPTIMISATION: 'Campagne-optimalisatie',
};

export function PlanPanel({ sessionId, projectId }: { sessionId: string | null; projectId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['copilot-plan', sessionId],
    enabled: Boolean(sessionId),
    queryFn: () =>
      apiFetch<{ plan: CopilotPlan | null; plans_migration_pending?: boolean }>(
        `/api/copilot/sessions/${sessionId}/plan`,
      ),
  });

  if (!sessionId) {
    return (
      <aside className={styles.contextPanel} aria-label="Plan">
        <h3 className={styles.contextTitle}>Plan</h3>
        <p className={styles.contextMuted}>Start een gesprek om een plan te maken.</p>
      </aside>
    );
  }

  if (q.isLoading) {
    return (
      <aside className={styles.contextPanel}>
        <h3 className={styles.contextTitle}>Plan</h3>
        <p className={styles.contextMuted}>Plan laden…</p>
      </aside>
    );
  }

  const plan = q.data?.plan;
  if (!plan) {
    return (
      <aside className={styles.contextPanel} aria-label="Plan">
        <h3 className={styles.contextTitle}>Plan</h3>
        <p className={styles.contextMuted}>
          {q.data?.plans_migration_pending
            ? 'Planning-engine wordt uitgerold.'
            : 'Geen actief plan in dit gesprek.'}
        </p>
      </aside>
    );
  }

  const aud = plan.planData?.audience;
  const size =
    aud?.companySize?.min != null && aud?.companySize?.max != null
      ? `${aud.companySize.min}–${aud.companySize.max} medewerkers`
      : 'nog niet gekozen';

  return (
    <>
    <aside className={styles.contextPanel} aria-label="Plan">
      <h3 className={styles.contextTitle}>{TYPE_LABEL[plan.type] ?? 'Plan'}</h3>
      <p className={styles.contextMeta}>
        Status: {STATUS_LABEL[plan.status] ?? plan.status} · v{plan.version}
      </p>
      <section>
        <h4>Doel</h4>
        <p className={styles.contextMuted}>{plan.objective.slice(0, 280)}</p>
      </section>
      {aud ? (
        <section>
          <h4>Doelgroep</h4>
          <ul className={styles.contextList}>
            {aud.description ? <li>{aud.description}</li> : null}
            {aud.locations?.length ? <li>{aud.locations.join(', ')}</li> : null}
            {aud.industries?.length ? <li>Sector: {aud.industries.join(', ')}</li> : null}
            <li>Bedrijfsgrootte: {size}</li>
            {aud.jobRoles?.length ? <li>Functie: {aud.jobRoles.join(', ')}</li> : null}
          </ul>
        </section>
      ) : null}
      <section>
        <h4>Campagne</h4>
        <p className={styles.contextMuted}>
          {plan.planData?.campaign?.channel
            ? `Kanaal: ${plan.planData.campaign.channel}`
            : 'Nog te ontwerpen'}
        </p>
      </section>
      {plan.missingFields.length ? (
        <section>
          <h4>Ontbreekt</h4>
          <ul className={styles.contextList}>
            {plan.missingFields.slice(0, 6).map((m) => (
              <li key={m.key}>{m.label}</li>
            ))}
          </ul>
        </section>
      ) : null}
      <button
        type="button"
        className={styles.sessionBtn}
        onClick={() => void qc.invalidateQueries({ queryKey: ['copilot-plan', sessionId] })}
      >
        Plan verversen
      </button>
    </aside>
    <ActionsPanel
      projectId={projectId}
      planId={plan.id}
      planStatus={plan.status}
      planVersion={plan.version}
    />
    </>
  );
}
